import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDialog } from '@gokrew/ui';
import { TranslationNS } from '@gokrew/models/constants';
import type { BillingSeatLimitDetails } from '@gokrew/models/billing';
import { usePermissions } from '@/context/PermissionContext';
import { BILLING_PATH, BILLING_SEAT_LIMIT_EVENT } from '@/constants/billing';
import { permissionKeys } from '@/utility';
import BillingPaywallDialog from '../BillingPaywallDialog';

/**
 * Opens the upgrade dialog whenever the API client reports a paywall 402
 * (add employee, hire from recruiting, reactivate). Mounted once in
 * `MainLayout`, inside the router, so "Add payment method" can navigate.
 * A second 402 while the dialog is still open does not stack another one.
 */
export function BillingPaywallListener() {
  const { t } = useTranslation(TranslationNS.Billing);
  const { openDialog, closeAll, dialogs } = useDialog();
  const { permissions } = usePermissions();
  const navigate = useNavigate();
  const canManageBilling = permissions.has(permissionKeys.billing.update);

  const paywallId = useRef<string | null>(null);
  const openIds = useRef<string[]>([]);
  useEffect(() => {
    openIds.current = dialogs.map((dialog) => dialog.id);
  }, [dialogs]);

  useEffect(() => {
    const onSeatLimit = (event: Event) => {
      if (paywallId.current && openIds.current.includes(paywallId.current)) {
        return;
      }

      const details =
        (event as CustomEvent<Partial<BillingSeatLimitDetails>>).detail ?? {};
      paywallId.current = openDialog({
        title: t('paywall.title'),
        content: (
          <BillingPaywallDialog
            details={details}
            canManageBilling={canManageBilling}
            onAddPaymentMethod={() => {
              // The form that was refused sits under this dialog; leave both.
              closeAll();
              navigate(BILLING_PATH);
            }}
          />
        ),
      });
      // Counted as open at once, before the provider re-renders.
      openIds.current = [...openIds.current, paywallId.current];
    };

    window.addEventListener(BILLING_SEAT_LIMIT_EVENT, onSeatLimit);
    return () =>
      window.removeEventListener(BILLING_SEAT_LIMIT_EVENT, onSeatLimit);
  }, [t, openDialog, closeAll, navigate, canManageBilling]);

  return null;
}
